import React, { useState, useRef, useEffect } from 'react';
import { LogOut } from 'lucide-react';
import type { User } from '../../contexts/AuthContext';

/** Role: Client, platform admin, firm Admin, or firm Staff. */
function roleLabel(user: User): string {
  if (user.role === 'client') return 'Client';
  if (user.loginType === 'Firm') {
    if (user.jwtRole === 'Staff') return 'Staff';
    return 'Admin';
  }
  return 'Admin';
}

function getInitials(name: string): string {
  const parts = (name || '').trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] || '').toUpperCase() + (parts[parts.length - 1][0] || '').toUpperCase();
  }
  if (parts[0]?.length >= 2) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0]?.[0] || '?').toUpperCase();
}

export default function UserMenu({ user, onLogout }: { user: User; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null as HTMLDivElement | null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const initials = getInitials(user.name);
  const roleText = roleLabel(user);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-200 text-sm font-medium text-gray-700 transition-colors hover:bg-emerald-50 hover:text-[var(--green)] focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
        aria-expanded={open}
        aria-haspopup="true"
        title={user.name}
      >
        {initials}
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 bg-white shadow-lg py-2 z-50"
          role="menu"
        >
          <div className="px-4 py-2 border-b border-gray-100 flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium text-gray-700 bg-gray-200 flex-shrink-0"
              aria-hidden
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-900 truncate" title={user.name}>
                {user.name}
              </p>
              <p className="text-xs text-gray-500 mt-0.5 break-words" title={`Role: ${roleText} (${user.loginType})`}>
                <span className="text-gray-400">Role</span>: {roleText} ({user.loginType})
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50 hover:text-red-800"
            role="menuitem"
          >
            <LogOut size={16} />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
